/**
 *
 * "Component for rendering a 3D canvas to
 * select replaced Panels for a PreShut Report with
 * Recent maintenance and B.O.M. table"
 *
 * @file   PreShutPanelSelect.js
 * @author Lateral
 * @since  2023
 */
import { Box, Button, Grid, Stack, Typography } from '@mui/material'
import BigNumber from 'bignumber.js'
import { DeckMode } from 'common/deckMode'
import BomTable from 'components/tables/bom/BomTable'
import { useMaintenance } from 'hooks/maintenance/useMaintenance'
import {
  addMaintenanceAction,
  addOtherKitMaintenance,
  findExisting,
  findExistingAndRemove,
  removeOtherKitMaintenance
} from 'pages/common/maintenance/logic'
import React, { useContext, useState, useEffect } from 'react'
import { useTheme } from '@mui/material/styles'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import RecentMaintenanceTable from 'components/tables/maintenance/RecentMaintenanceTable'
import { ReportingContext } from 'components/reportingContext/ReportingContext'
import { MobileDeckCanvas } from 'components/mobileDeckCanvas/MobileDeckCanvas'
import { useOnBomClick } from 'hooks/useOnBomClick'
import { KitQuantityDialog } from './KitQuantityDialog'
import { enqueueSnackbar } from 'notistack'

function PreShutPanelSelect({
  selectedPreShutDeck,
  setSelectedPreShutDeck,
  preShutDecks,
  onSetPanelLayout,
  setIsReportDirty,
  setSelected,
  selected,
  otherKitsSelected,
  setOtherKitsSelected
}) {
  /**
   * Table for viewing all Reporting history.
   *
   * @function
   *
   * @param {} setSelectedPreShutDeck -To update selectedPreShutDecks hook
   * @param {object[]} preShutDecks - Selected deck layouts
   * @param {} setPreShutDecks - To update preShutDecks hook
   * @param {} onUpdatePreShutDeck - Action on update PreShutDeck hook
   * @param {} onSetPanelLayout - Action on det PanelLayout hook
   * @param {} setIsReportDirty - To update isReportDirty hook
   *
   * @returns {object} - Components for Pre-Shut Report panel selection page
   */
  const { materialSelected, onBomClick, setMaterialSelected } = useOnBomClick(() => {
    setSelected([])
  }, true)

  const { setShowTabs } = useContext(ReportingContext)
  const { actions } = useMaintenance()
  const theme = useTheme()
  const index = preShutDecks.findIndex((p) => p.id === selectedPreShutDeck)
  const preShutDeck = preShutDecks.find((p) => p.id === selectedPreShutDeck)
  const deckRevisionHistory = preShutDeck.PanelLayout
  // back and forward button
  const [scrollY, setScrollY] = useState(0)
  const [selectedKit, setSelectedKit] = useState(null)

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY)
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  //Panel logic is much simpler here. Only Replace can be selected, no radial menu required.
  function onSelectChange(data) {      
    if (data.length > 0) {
      let clone = structuredClone(deckRevisionHistory)
      for (const item of data) {
        const panel = item.Position ? item : null
        const sideliner = item.StartPosition !== null && item.StartPosition !== undefined ? item : null
        const existing = findExisting(clone, panel, sideliner)
        if (!Object.entries(existing).length) {
          const result = addMaintenanceAction(actions.Replace, -1, [item], { deckRevisionHistory: clone }, actions)
          clone = result.clone.deckRevisionHistory
        }
      }
      const toRemove = selected.filter((s) => !data.find((d) => JSON.stringify(d) === JSON.stringify(s)))
      for (const item of toRemove) {
        const panel = item.Position ? item : null
        const sideliner = item.StartPosition !== null && item.StartPosition !== undefined ? item : null
        findExistingAndRemove(clone, panel, sideliner)
      }
      onSetPanelLayout(clone)
      setSelected((current) => {
        return current.concat(data)
      })
    } else {
      const clone = structuredClone(deckRevisionHistory)
      for (const item of selected) {
        const panel = item.Position ? item : null
        const sideliner = item.StartPosition !== null && item.StartPosition !== undefined ? item : null
        findExistingAndRemove(clone, panel, sideliner)
      }
      onSetPanelLayout(clone)
    }
    setMaterialSelected('')
    setSelected([])
  }

  const onOtherKitClick = (kit) => {
    if (otherKitsSelected.includes(kit.MaterialNumber)) {
      onSetPanelLayout(removeOtherKitMaintenance([kit.MaterialNumber], { deckRevisionHistory }).deckRevisionHistory)
      setOtherKitsSelected((current) => current.filter((k) => k !== kit.MaterialNumber))
    } else {
      if (kit.Quantity > 0) {
        // Show dialog to ask for quantity
        setSelectedKit(kit)
      } else {
        enqueueSnackbar({
          message: 'Kit has zero quantity available',
          variant: 'info'
        })
      }
    }
  }

  const handleKitQuantitySubmit = (quantity) => {
    if (selectedKit) {
      const clone = addOtherKitMaintenance(
        [{ materialNumber: selectedKit.MaterialNumber, quantity: Number(quantity) }],
        { deckRevisionHistory },
        actions.Replace.id
      )
      onSetPanelLayout(clone.deckRevisionHistory)
      setOtherKitsSelected((current) => current.concat([selectedKit.MaterialNumber]))
      setSelectedKit(null)
    }
  }

  // Action on clicking on a panel
  function onPanelClick(data) {
    if (!data || !data.length || data === 'cancel') {
      const clone = structuredClone(deckRevisionHistory)
      for (const item of selected) {
        const panel = item.Position ? item : null
        const sideliner = item.StartPosition !== null && item.StartPosition !== undefined ? item : null
        findExistingAndRemove(clone, panel, sideliner)
      }
      onSetPanelLayout(clone)
      setSelected([])
      return
    }
    if (Array.isArray(data) && data.length > 0) {
      data = data[0]
    }
    setIsReportDirty(true)
    const panel = data.Position ? data : null
    const sideliner = data.StartPosition !== null && data.StartPosition !== undefined ? data : null
    const clone = structuredClone(deckRevisionHistory)

    const existing = findExistingAndRemove(clone, panel, sideliner)

    if (Object.entries(existing).length) {
      const getKey = (data) =>
        typeof data.StartPosition === 'undefined'
          ? `${data.Position.Row}-${data.Position.Column}`
          : `${data.StartPosition}-${data.Side}`

      setSelected((current) => {
        return current.filter((panel) => getKey(panel) !== getKey(data))
      })
      onSetPanelLayout(clone)
    } else {
      const result = addMaintenanceAction(
        actions.Replace,
        -1,
        [data],
        { deckRevisionHistory: deckRevisionHistory },
        actions
      )
      onSetPanelLayout(result.clone.deckRevisionHistory)
      setSelected((current) => {
        return current.concat([data])
      })
    }
    setMaterialSelected('')
  }

  // // Action on clicking 'Inspect Deck Rails'
  // function onSwitchDeckRails() {
  //   preShutDeck.InspectDeckRails = !preShutDeck.InspectDeckRails
  //   onUpdatePreShutDeck(preShutDeck)
  // }

  // // Action on clicking 'Inspect Crossbeams'
  // function onSwitchCrossbeams() {
  //   preShutDeck.InspectCrossbeams = !preShutDeck.InspectCrossbeams
  //   onUpdatePreShutDeck(preShutDeck)
  // }

  // Action on clicking 'Previous Deck' button
  function onPreviousDeck() {
    if (index === 0) {
      setSelectedPreShutDeck('')
      setShowTabs(true)
    } else {
      const nextIndex = new BigNumber(index).minus(1).toNumber()
      const nextDeck = preShutDecks[nextIndex]
      setSelectedPreShutDeck(nextDeck.id)
    }
  }

  // Action on clicking 'Next Deck' button
  function onNextDeck() {
    if (new BigNumber(preShutDecks.length).minus(1).isEqualTo(index)) {
      setSelectedPreShutDeck('')
      setShowTabs(true)
    } else {
      const nextIndex = new BigNumber(index).plus(1).toNumber()
      const nextDeck = preShutDecks[nextIndex]
      setSelectedPreShutDeck(nextDeck.id)
    }
  }

  return (
    <Grid container gap={1} sx={{ height: '100%', width: '100%' }}>
      <Grid item md={5} xs={12} sx={{ height: '100%' }}>
        <Typography className="title" variant="h5" component="h5" sx={{ color: 'text.secondary' }}>
          {preShutDeck.Name}
        </Typography>
        <Box sx={{ height: '100%' }}>
          <MobileDeckCanvas
            data={preShutDeck.DeckRevision}
            maintenance={deckRevisionHistory}
            mouseCanvasPosition={{ x: 0, y: 0 }}
            onPanelClick={onPanelClick}
            selected={selected}
            materialSelected={materialSelected}
            onSelectChange={onSelectChange}
            awaitingActionId={-1}
            mode={DeckMode.maintenance}
            type="report"
          />
        </Box>
      </Grid>
      <Grid item md={6} xs={12} sx={{ height: '100%' }}>
        <Box sx={{ height: '20%', marginBottom: '2em' }}>
          <RecentMaintenanceTable
            deckRevision={preShutDeck.DeckRevision}
            deckRevisionHistories={preShutDeck.Histories}
            isMobile={false}
          />
        </Box>
        <Box sx={{ height: '65%' }}>
          <BomTable
            data={preShutDeck.DeckRevision}
            selected={[]}
            materialSelected={materialSelected}
            onClick={onBomClick}
            onOtherKitClick={onOtherKitClick}
            otherKitsSelected={otherKitsSelected}
          />
        </Box>
        <Stack direction="row" alignItems="center" sx={{ width: '100%' }}>
          <Stack
            direction="row"
            spacing={2}
            alignItems="end"
            justifyContent="end"
            sx={{
              position: 'fixed',
              bottom: Math.max(10, 50 - scrollY * 0.1) + 'px', // Moves up slightly
              right: 20,
              transition: 'bottom 0.3s ease-out'
            }}>
            <Button
              onClick={onPreviousDeck}
              startIcon={<ArrowBackIcon />}
              sx={{ color: `${theme.palette.secondary.main}` }}>
              {index === 0 ? 'Back' : 'Previous Deck'}
            </Button>
            <Button
              onClick={onNextDeck}
              endIcon={<ArrowForwardIcon />}
              sx={{ color: `${theme.palette.secondary.main}` }}>
              {index === preShutDecks.length - 1 ? 'Done' : 'Next Deck'}
            </Button>
          </Stack>
        </Stack>
      </Grid>
      {selectedKit && (
        <KitQuantityDialog kit={selectedKit} onClose={() => setSelectedKit(null)} onSubmit={handleKitQuantitySubmit} />
      )}
    </Grid>
  )
}

export default PreShutPanelSelect
